import { useEffect, useMemo, useState } from "react";
import { useLocalDate } from "@/hooks/useLocalDate";

export const CALENDAR_API = `${process.env.REACT_APP_BACKEND_URL}/api/calendar`;
export const useChurchCalendar = () => {
  const today = useLocalDate();
  const [catalog, setCatalog] = useState([]);
  const [selection, setSelection] = useState("all");
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(null);
  const [category, setCategory] = useState("all");
  const [showWeekdays, setShowWeekdays] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${CALENDAR_API}/catalog`, { signal: controller.signal }).then((r) => {
      if (!r.ok) throw new Error("The calendar editions could not be loaded.");
      return r.json();
    }).then((items) => {
      setCatalog(items);
      if (items.some((item) => !item.years.includes(today.getFullYear()))) setSelection("roman-of");
    }).catch((e) => { if (e.name !== "AbortError") { setError("The calendar is temporarily unavailable. Please try again."); setLoading(false); } });
    return () => controller.abort();
  }, [retry, today]);
  const editions = useMemo(() => catalog.filter((c) => selection === "all" || c.id === selection || (selection === "eastern" && c.family === "east")), [catalog, selection]);
  const years = useMemo(() => editions.length ? editions[0].years.filter((y) => editions.every((c) => c.years.includes(y))) : [], [editions]);
  useEffect(() => {
    if (!catalog.length) return;
    const controller = new AbortController();
    setLoading(true); setError(""); setRecords([]);
    fetch(`${CALENDAR_API}/events?year=${year}&calendar=${selection}`, { signal: controller.signal }).then(async (response) => {
      if (!response.ok) { const body = await response.json(); throw new Error(typeof body.detail === "string" ? body.detail : "The calendar could not be loaded."); }
      return response.json();
    }).then(setRecords).catch((e) => { if (e.name !== "AbortError") setError("The calendar is temporarily unavailable. Please try again."); }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [catalog, selection, year, retry]);
  const chooseCalendar = (id) => {
    const choices = catalog.filter((c) => id === "all" || c.id === id || (id === "eastern" && c.family === "east"));
    const available = choices.length ? choices[0].years.filter((y) => choices.every((c) => c.years.includes(y))) : [];
    if (!available.includes(year) && available.length) setYear(available.includes(today.getFullYear()) ? today.getFullYear() : available[available.length - 1]);
    setSelection(id); setSelectedDay(null);
  };
  const changeMonth = (value) => {
    let y = year, m = value;
    if (m < 1) { y--; m = 12; } else if (m > 12) { y++; m = 1; }
    if (!years.includes(y)) return;
    setMonth(m); setYear(y); setSelectedDay(null);
  };
  const filtered = useMemo(() => records.filter((f) => f.month === month && (category === "all" || f.category === category) && (showWeekdays || !f.is_feria || f.category !== "other")), [records, month, category, showWeekdays]);
  const jumpToday = () => {
    const currentYear = today.getFullYear();
    if (!years.includes(currentYear)) setSelection("roman-of");
    setYear(currentYear);
    setMonth(today.getMonth() + 1); setSelectedDay(today.getDate());
  };
  return { today, catalog, editions, selection, chooseCalendar, year, setYear, years, month, changeMonth, selectedDay, setSelectedDay, category, setCategory,
    showWeekdays, setShowWeekdays, records, filtered, loading, error, jumpToday, retry: () => setRetry((x) => x + 1) };
};